import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  TrendingUp,
  ArrowLeft,
  Users,
  DollarSign,
  Activity,
  Shield,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  ExternalLink,
  Bell,
  BellOff
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API, AuthContext } from "@/App";

// Notification sound using Web Audio API
const playNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // Create a pleasant "ding" sound
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.setValueAtTime(880, audioContext.currentTime); // A5 note
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.5);
    
    // Play a second higher note for a pleasant "ding-ding"
    setTimeout(() => {
      const osc2 = audioContext.createOscillator();
      const gain2 = audioContext.createGain();
      osc2.connect(gain2);
      gain2.connect(audioContext.destination);
      osc2.frequency.setValueAtTime(1108, audioContext.currentTime); // C#6 note
      osc2.type = 'sine';
      gain2.gain.setValueAtTime(0.3, audioContext.currentTime);
      gain2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      osc2.start(audioContext.currentTime);
      osc2.stop(audioContext.currentTime + 0.5);
    }, 150);
  } catch (e) {
    console.log('Audio not supported:', e);
  }
};

const AdminPanel = () => {
  const { user } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [lastUserCount, setLastUserCount] = useState(null);
  const isFirstLoad = useRef(true);

  // Poll for new users every 30 seconds
  useEffect(() => {
    fetchData();
    
    const interval = setInterval(() => {
      fetchDataSilent();
    }, 30000); // Check every 30 seconds
    
    return () => clearInterval(interval);
  }, []);

  // Check for new users and play sound
  useEffect(() => {
    if (stats?.total_users !== undefined && lastUserCount !== null) {
      if (stats.total_users > lastUserCount && !isFirstLoad.current && soundEnabled) {
        playNotificationSound();
        toast.success(`🎉 New user registered! Total: ${stats.total_users}`, {
          duration: 5000,
        });
      }
    }
    if (stats?.total_users !== undefined) {
      setLastUserCount(stats.total_users);
      isFirstLoad.current = false;
    }
  }, [stats?.total_users, soundEnabled]);

  const fetchDataSilent = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [statsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/users`, { headers }),
      ]);
      
      setStats(statsRes.data);
      setUsers(usersRes.data.users || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const [statsRes, usersRes, paymentsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/admin/payments`, { headers })
      ]);

      setStats(statsRes.data);
      setUsers(usersRes.data.users || []);
      setPayments(paymentsRes.data.payments || []);
    } catch (error) {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/admin/users/${userId}/role?role=${newRole}`,
        {},
        {
          
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      toast.success("Role updated successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to update role");
    }
  };

  const confirmPayment = async (paymentId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/admin/payments/${paymentId}/confirm`,
        {},
        {
          
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      toast.success("Payment confirmed and subscription activated");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to confirm payment");
    }
  };

  const triggerSubscriptionCheck = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/admin/check-subscriptions`,
        {},
        {
          
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      toast.success("Subscription check completed");
      fetchData();
    } catch (error) {
      toast.error("Failed to run subscription check");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString();
  };

  // Check if user is new: registered within 24h AND not seen by admin
  const isNewUser = (user) => {
    // If admin has seen this user, they're not "new" anymore
    if (user.admin_seen) return false;
    
    // Otherwise, check if they registered within 24 hours
    if (!user.created_at) return false;
    const created = new Date(user.created_at);
    const now = new Date();
    const hoursDiff = (now - created) / (1000 * 60 * 60);
    return hoursDiff <= 24;
  };

  // Mark all users as seen
  const markAllUsersSeen = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/admin/users/mark-seen`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("All users marked as seen");
      fetchData();
    } catch (error) {
      toast.error("Failed to mark users as seen");
    }
  };

  // Count unseen new users
  const getUnseenNewUsersCount = () => {
    return users.filter(u => isNewUser(u)).length;
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case "confirmed":
        return "bg-green-500/20 text-green-400";
      case "pending":
        return "bg-amber-500/20 text-amber-400";
      case "failed":
        return "bg-red-500/20 text-red-400";
      default:
        return "bg-secondary text-muted-foreground";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Navigation */}
      <nav className="glass sticky top-0 z-50 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-white transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span>Dashboard</span>
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-amber-500" />
            <span className="font-bold">Admin Panel</span>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-black">ADMIN DASHBOARD</h1>
            <p className="text-muted-foreground mt-2">
              Manage users, payments, and subscriptions
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant={soundEnabled ? "default" : "outline"}
              onClick={() => {
                setSoundEnabled(!soundEnabled);
                if (!soundEnabled) {
                  playNotificationSound(); // Test sound when enabling
                  toast.success("Notifications enabled - you'll hear a sound when new users register");
                } else {
                  toast.info("Notifications muted");
                }
              }}
              className={`rounded-none ${soundEnabled ? 'bg-green-600 hover:bg-green-700' : ''}`}
              title={soundEnabled ? "Notifications ON - Click to mute" : "Notifications OFF - Click to enable"}
            >
              {soundEnabled ? (
                <>
                  <Bell className="w-4 h-4 mr-2" />
                  Sound ON
                </>
              ) : (
                <>
                  <BellOff className="w-4 h-4 mr-2" />
                  Sound OFF
                </>
              )}
            </Button>
            <Button 
              variant="outline" 
              onClick={triggerSubscriptionCheck}
              className="rounded-none"
            >
              <Clock className="w-4 h-4 mr-2" />
              Check Expiring
            </Button>
            <Button 
              variant="outline" 
              onClick={fetchData}
              className="rounded-none"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <div className="bg-card border border-border p-4">
            <div className="flex items-center gap-2 mb-1">
              <Users className="w-4 h-4 text-blue-500" />
              <span className="text-xs text-muted-foreground">Total Users</span>
            </div>
            <div className="text-3xl font-mono font-bold" data-testid="total-users">
              {stats?.total_users || 0}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              +{stats?.recent_signups_24h || 0} today · +{stats?.weekly_signups || 0} this week
            </div>
          </div>
          
          <div className="bg-card border border-border p-4">
            <div className="flex items-center gap-2 mb-1">
              <Shield className="w-4 h-4 text-green-500" />
              <span className="text-xs text-muted-foreground">Pro Subscribers</span>
            </div>
            <div className="text-3xl font-mono font-bold text-green-500" data-testid="active-subs">
              {stats?.active_subscribers || 0}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {stats?.free_users || 0} free users
            </div>
          </div>
          
          <div className="bg-card border border-border p-4">
            <div className="flex items-center gap-2 mb-1">
              <Activity className="w-4 h-4 text-purple-500" />
              <span className="text-xs text-muted-foreground">Signals Generated</span>
            </div>
            <div className="text-3xl font-mono font-bold text-purple-500" data-testid="total-signals">
              {stats?.total_signals || 0}
            </div>
          </div>
          
          <div className="bg-card border border-border p-4">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="w-4 h-4 text-amber-500" />
              <span className="text-xs text-muted-foreground">Total Revenue</span>
            </div>
            <div className="text-3xl font-mono font-bold text-amber-500" data-testid="total-revenue">
              ${stats?.total_revenue || 0}
            </div>
          </div>

          <div className="bg-card border border-border p-4">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-xs text-muted-foreground">Confirmed</span>
            </div>
            <div className="text-3xl font-mono font-bold text-green-500" data-testid="confirmed-payments">
              {stats?.confirmed_payments || 0}
            </div>
          </div>

          <div className="bg-card border border-border p-4">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-amber-500" />
              <span className="text-xs text-muted-foreground">Pending</span>
            </div>
            <div className="text-3xl font-mono font-bold text-amber-500" data-testid="pending-payments">
              {stats?.pending_payments || 0}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-card border border-border rounded-none mb-6">
            <TabsTrigger value="overview" className="rounded-none">Users</TabsTrigger>
            <TabsTrigger value="payments" className="rounded-none">Payments</TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="overview">
            <div className="bg-card border border-border">
              <div className="p-4 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <h2 className="font-bold">User Management</h2>
                  {getUnseenNewUsersCount() > 0 && (
                    <span className="px-2 py-1 text-xs bg-red-500/20 text-red-400 font-bold">
                      {getUnseenNewUsersCount()} NEW
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {getUnseenNewUsersCount() > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={markAllUsersSeen}
                      className="text-xs"
                      data-testid="mark-all-seen-btn"
                    >
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Mark All Seen
                    </Button>
                  )}
                  <span className="text-sm text-muted-foreground">
                    {users.length} users
                  </span>
                </div>
              </div>
              
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-muted-foreground">User</TableHead>
                    <TableHead className="text-muted-foreground">Email</TableHead>
                    <TableHead className="text-muted-foreground">Role</TableHead>
                    <TableHead className="text-muted-foreground">Status</TableHead>
                    <TableHead className="text-muted-foreground">Trials</TableHead>
                    <TableHead className="text-muted-foreground">Expires</TableHead>
                    <TableHead className="text-muted-foreground">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow 
                      key={u.user_id} 
                      className="border-border"
                      data-testid={`user-row-${u.user_id}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="relative">
                            {u.picture ? (
                              <img 
                                src={u.picture} 
                                alt={u.name}
                                className="w-8 h-8 rounded-full"
                              />
                            ) : (
                              <div className="w-8 h-8 bg-secondary flex items-center justify-center">
                                {u.name?.charAt(0)?.toUpperCase() || "?"}
                              </div>
                            )}
                            {isNewUser(u) && (
                              <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse" title="New user - not seen"></span>
                            )}
                          </div>
                          <div>
                            <span className="font-medium">{u.name}</span>
                            {isNewUser(u) && (
                              <span className="ml-2 text-xs text-red-400">NEW</span>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{u.email}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 text-xs font-bold uppercase ${
                          u.role === "admin"
                            ? "bg-amber-500/20 text-amber-400"
                            : "bg-secondary text-muted-foreground"
                        }`}>
                          {u.role}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 text-xs font-bold uppercase ${
                          u.subscription_status === "active"
                            ? "bg-green-500/20 text-green-400"
                            : u.subscription_status === "expired"
                              ? "bg-red-500/20 text-red-400"
                              : "bg-secondary text-muted-foreground"
                        }`}>
                          {u.subscription_status}
                        </span>
                      </TableCell>
                      <TableCell className="font-mono">
                        {u.free_trials_remaining}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {u.subscription_end_date ? formatDate(u.subscription_end_date) : "—"}
                      </TableCell>
                      <TableCell>
                        {u.user_id !== user?.user_id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => updateUserRole(
                              u.user_id, 
                              u.role === "admin" ? "user" : "admin"
                            )}
                            className="text-xs"
                            data-testid={`toggle-role-${u.user_id}`}
                          >
                            {u.role === "admin" ? "Remove Admin" : "Make Admin"}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments">
            <div className="bg-card border border-border">
              <div className="p-4 border-b border-border flex items-center justify-between">
                <h2 className="font-bold">Payment Transactions</h2>
                <span className="text-sm text-muted-foreground">
                  {payments.length} transactions
                </span>
              </div>
              
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-muted-foreground">Date</TableHead>
                    <TableHead className="text-muted-foreground">User</TableHead>
                    <TableHead className="text-muted-foreground">Method</TableHead>
                    <TableHead className="text-muted-foreground">Amount</TableHead>
                    <TableHead className="text-muted-foreground">Status</TableHead>
                    <TableHead className="text-muted-foreground">TX Hash</TableHead>
                    <TableHead className="text-muted-foreground">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map((payment) => (
                    <TableRow 
                      key={payment.payment_id} 
                      className="border-border"
                    >
                      <TableCell className="text-sm">
                        {formatDate(payment.created_at)}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {payment.email}
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 text-xs font-bold ${
                          payment.payment_method?.includes("TRC20")
                            ? "bg-red-500/20 text-red-400"
                            : "bg-blue-500/20 text-blue-400"
                        }`}>
                          {payment.payment_method || payment.network}
                        </span>
                      </TableCell>
                      <TableCell className="font-mono">
                        ${payment.verified_amount || payment.claimed_amount || "30"}
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 text-xs font-bold uppercase ${getPaymentStatusColor(payment.payment_status)}`}>
                          {payment.payment_status}
                        </span>
                      </TableCell>
                      <TableCell>
                        {payment.tx_hash && (
                          <a
                            href={payment.network === "TRC20" 
                              ? `https://tronscan.org/#/transaction/${payment.tx_hash}`
                              : `https://etherscan.io/tx/${payment.tx_hash}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-400 hover:underline flex items-center gap-1"
                          >
                            {payment.tx_hash.substring(0, 10)}...
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </TableCell>
                      <TableCell>
                        {payment.payment_status === "pending" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => confirmPayment(payment.payment_id)}
                            className="text-xs text-green-400 hover:text-green-300"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Confirm
                          </Button>
                        )}
                        {payment.payment_status === "failed" && (
                          <span className="text-xs text-muted-foreground">
                            {payment.failure_reason?.substring(0, 30)}...
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPanel;
